import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "./api";

type AuthStatus = "loading" | "authed" | "anon";

type AuthCtx = {
  status: AuthStatus;
  sub: string | null;
  refresh: () => Promise<void>;
  login: (payload: { password?: string; invite?: string }) => Promise<void>;
  logout: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [sub, setSub] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      if (me.authenticated) {
        setStatus("authed");
        setSub(me.sub || "user");
      } else {
        setStatus("anon");
        setSub(null);
      }
    } catch {
      setStatus("anon");
      setSub(null);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (payload: { password?: string; invite?: string }) => {
    await api.login(payload);
    await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    await api.logout();
    setStatus("anon");
    setSub(null);
  }, []);

  const value = useMemo(
    () => ({ status, sub, refresh, login, logout }),
    [status, sub, refresh, login, logout]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside provider");
  return v;
}
